import { createFeatureSelector, createSelector } from '@ngrx/store';

import { RoomWithMembers } from './chat.models';
import { CHAT_FEATURE_KEY, ChatState, chatAdapter } from './chat.reducer';

// Lookup the 'Chat' feature state managed by NgRx
export const selectChatState = createFeatureSelector<ChatState>(CHAT_FEATURE_KEY);

const { selectAll, selectEntities } = chatAdapter.getSelectors();

export const selectChatLoaded = createSelector(selectChatState, (state: ChatState) => state.loaded);

export const selectChatError = createSelector(selectChatState, (state: ChatState) => state.error);

export const selectAllChat = createSelector(selectChatState, (state: ChatState) =>
  selectAll(state),
);

export const selectChatEntities = createSelector(selectChatState, (state: ChatState) =>
  selectEntities(state),
);

export const selectSelectedId = createSelector(
  selectChatState,
  (state: ChatState) => state.selectedId,
);

export const selectEntity = createSelector(
  selectChatEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
);

export const getChatRoomsByType = (type: string) =>
  createSelector(selectChatState, (state: ChatState) => {
    if (!state.rooms[type]) {
      return {
        pagination: {
          currentPage: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
        },
        rooms: [],
      };
    }
    return {
      pagination: state.rooms[type].pagination,
      rooms: state.rooms[type].rooms.map((room) => {
        return {
          ...room,
          members: state.roomMembers[room.id],
        };
      }),
    };
  });

export const getChatMessagesByRoomId = (roomId: number) =>
  createSelector(selectChatState, (state: ChatState) => state.messages[roomId]);

export const getSelectedRoom = createSelector(selectChatState, (state: ChatState) => {
  const foundKey = Object.keys(state.rooms).find((key) =>
    state.rooms[key].rooms.some((room) => room.id === state.selectedRoomId),
  );
  return foundKey
    ? state.rooms[foundKey].rooms.find((room) => room.id === state.selectedRoomId)
    : state?.rooms[Object.keys(state?.rooms)?.[0]]?.rooms?.[0];
});

export const getSelectedRoomId = createSelector(selectChatState, (state: ChatState) => {
  return state.selectedRoomId;
});

export const getRoomMembers = (roomId: number) =>
  createSelector(selectChatState, (state: ChatState) => state?.roomMembers[roomId] || []);

export const getAllRoomMembers = createSelector(
  selectChatState,
  (state: ChatState) => state?.roomMembers,
);

export const getRoomWithMembers = (roomId: number) =>
  createSelector(selectChatState, (state: ChatState) => {
    let room: RoomWithMembers | undefined;

    for (const key of Object.keys(state.rooms)) {
      const foundRoom = state.rooms[key].rooms.find((r) => r.id === roomId);
      if (foundRoom) {
        room = foundRoom;
        break;
      }
    }

    if (!room) {
      return undefined; // or return a default RoomWithMembers object
    }

    return {
      ...room,
      members: state.roomMembers[room.id],
    };
  });

export const getRoomLoading = (roomType: string) =>
  createSelector(selectChatState, (state: ChatState) => state?.roomLoading?.[roomType]);

export const getMessageLoading = (roomId: number) =>
  createSelector(selectChatState, (state: ChatState) => state?.messageLoading?.[roomId]);
